<template>
  <nav
    id="navigation"
    class="main-navigation"
  >
    <ul
      :class="setStickyStyling ? 'lg:opacity-0 lg:pointer-events-none' : 'opacity-100'"
      class="main-navigation-list transition-opacity pt-4"
    >
      <li
        v-if="navMainSearchAction"
        class="main-navigation-list-item main-navigation-list-item--search mb-4 mb:mb-0"
      >
        <kf-search-bar
          class="search-bar-wrapper-small"
          :search-action="navMainSearchAction"
          :search-placeholder="navMainSearchPlaceholder"
        />
      </li>

      <div
        v-if="navMainCta && navMainCta.length > 1"
        class="flex lg:hidden flex-row gap-2 w-full flex-wrap items-center mb-6"
      >
        <kf-link
          v-for="(link, index) in navMainCta"
          :key="`${link.label}-${index}`"
          :href="link.href"
          type="button-secondary"
          class="h-12 text-base w-fit"
          :class="{
            'first-button': index === 0,
            'single-button': navMainCta.length <= 1,
          }"
          @handleClick="fireEvent(link.trackData)"
        >
          <span class="flex justify-center items-center gap-2">
            <img
              v-if="link.icon"
              :src="link.icon"
              :alt="link.label"
              class="h-4 w-4"
            ><span>{{
              link.label
            }}</span>
          </span>
        </kf-link>
      </div>
      <li
        v-for="(item, index) in navMainItems"
        :key="item.Name + '-' + index"
        class="main-navigation-list-item"
      >
        <a
          v-if="item.UrlSegment"
          :href="`${item.UrlSegment}`"
          :target="item.target ? '_blank' : null"
          class="main-navigation-list-link"
          :class="{ ['is-active']: isUrlCurrentlyActive(item.UrlSegment) }"
          @click="scrollToElementIfLinkIsLocal(item.UrlSegment, $event)"
        >
          {{ item.Name }}
        </a>
        <button
          v-else
          :id="`main-nav-item-${index}`"
          class="main-navigation-list-link is-toggle"
          :class="{ 'is-open': index === selectedSub && navSubState }"
          :aria-expanded="index === selectedSub && navSubState"
          :aria-controls="`submenu-${index}`"
          :tabindex="0"
          @click.prevent="openSub(item, index)"
          @keydown.down.prevent="openSubAndFocus(item, index)"
          @keydown.enter.prevent="openSubAndFocus(item, index)"
          @keydown.space.prevent="openSubAndFocus(item, index)"
          @keydown.esc.prevent="closeSub()"
          @focus="handleFocus(item, index)"
        >
          {{ item.Name }}
        </button>

        <!-- Desktop submenu (text only + icons) -->
        <div
          v-if="useCompactNavigation"
          v-show="index === selectedSub && navSubState"
          class="hidden lg:block absolute top-12 z-10 left-0 bg-white px-6 pt-5 pb-4 rounded-sm shadow w-max"
        >
          <ul
            v-if="item.navigationSub.length"
            :id="`submenu-${index}`"
            class="sub-navigation-list"
            :class="{ 'is-open': index === selectedSub && navSubState }"
            role="menu"
          >
            <!-- text-only links -->
            <li
              v-for="subItem in item.navigationSub"
              :key="subItem.label + 'desktop-' + index"
              class="h-9 flex items-center"
              role="none"
            >
              <a
                :href="subItem.href"
                class="no-underline text-base text-primary-bravo h-full w-full hover:text-primary-alpha font-medium"
                role="menuitem"
              >
                {{ subItem.label }}
              </a>
            </li>

            <!-- divider -->
            <hr
              v-if="item.navigationSubWithIcons && item.navigationSubWithIcons.length"
              class="mt-1 mb-3 border-t border-gray-100/25"
            >

            <!-- icon-enhanced links -->
            <li
              v-for="subItem in item.navigationSubWithIcons"
              :key="subItem.label + 'desktop-icon-' + index"
              class="h-10 flex items-center"
              role="none"
            >
              <a
                :href="subItem.href"
                class="flex flex-row gap-3 items-center no-underline text-sm text-primary-bravo h-full w-full font-medium"
                role="menuitem"
              >
                <img
                  :src="subItem.icon"
                  :alt="subItem.label"
                  class="h-6 w-6 object-cover"
                >
                <span>{{ subItem.label }}</span>
              </a>
            </li>
          </ul>
        </div>

        <!-- Mobile submenu  -->

        <ul
          v-if="item.navigationSub.length || item.navigationSubWithIcons.length"
          class="sub-navigation-list-small"
          :class="{ 'is-visible': index === selectedSub }"
        >
          <!-- text‑only links -->
          <template v-for="subItem in item.navigationSub">
            <li
              v-if="subItem.href && subItem.label"
              :key="subItem.label + '-mobile-' + index"
              class="sub-navigation-list-item-small mb-3"
            >
              <a
                :href="subItem.href"
                class="sub-navigation-list-link-small font-medium"
              >
                {{ subItem.label }}
              </a>
            </li>
          </template>

          <!-- divider, mobile‑only -->
          <hr
            v-if="item.navigationSubWithIcons && item.navigationSubWithIcons.length > 0"
            class="border-t border-gray-100/25 my-2 lg:hidden"
          >


          <!-- icon‑enhanced links -->
          <template v-for="subItem in item.navigationSubWithIcons">
            <li
              v-if="subItem.href && subItem.label"
              :key="subItem.label + '-mobile-icon-' + index"
              class="sub-navigation-list-item-small text-sm font-medium flex items-center gap-3 mb-3"
            >
              <img
                :src="subItem.icon"
                :alt="subItem.label"
                class="h-6 w-6 object-cover flex-shrink-0"
              >
              <a
                :href="subItem.href"
                class="sub-navigation-list-link-small flex-1"
              >
                {{ subItem.label }}
              </a>
            </li>
          </template>
        </ul>
      </li>
    </ul>
    <div class="main-navigation-end">
      <kf-search-bar
        v-if="navMainSearchAction"
        :search-action="navMainSearchAction"
        :search-placeholder="navMainSearchPlaceholder"
      />
      <div class="hidden lg:flex flex-row gap-2 mb-2">
        <kf-link
          v-for="(link, index) in navMainCta"
          :key="`${link.label}-${index}`"
          :href="link.href"
          type="button-secondary"
          class="h-12 text-base w-fit"
          :class="{
            'first-button': index === 0,
            'single-button': navMainCta.length <= 1
          }"
          @handleClick="fireEvent(link.trackData)"
        >
          <span class="flex justify-center items-center gap-2">
            <img
              v-if="link.icon"
              :src="link.icon"
              :alt="link.label"
              class="h-4 w-4"
            ><span>{{
              link.label
            }}</span>
          </span>
        </kf-link>
      </div>
    </div>
    <slot />
  </nav>
</template>

<script>
import KfLink from '@/components/base/link/Link.vue';
import KfSearchBar from '@/components/search/SearchBar.vue';
import { trackEvent } from '@/lib/gtm/trackEvent.js';

export default {
  name: 'KfNavigationMain',
  components: {
    KfSearchBar,
    KfLink,
  },
  props: {
    setStickyStyling: {
      type: Boolean,
      default: true,
    },
    navMainItems: {
      type: Array,
      default: null,
    },
    navMainSearchAction: {
      type: String,
      default: null,
    },
    navMainSearchPlaceholder: {
      type: String,
      default: null,
    },
    navMainCta: {
      type: Array,
      default: null,
    },
    navSubState: {
      type: Boolean,
      default: null,
    },
    useCompactNavigation: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      selectedSub: null,
      localNavSubState: false,
      mouseEventRecent: false,
    };
  },

  mounted() {
    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('mousedown', this.handleMouseEvent);
    document.addEventListener('keydown', this.handleKeyDown);

    this.mouseEventRecent = false;
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('mousedown', this.handleMouseEvent);
    document.removeEventListener('keydown', this.handleKeyDown);
  },

  methods: {
    handleMouseEvent() {
      this.mouseEventRecent = true;

      setTimeout(() => {
        this.mouseEventRecent = false;
      }, 100);
    },

    handleKeyDown(event) {
      if (event.key === 'Tab') {
        this.mouseEventRecent = false;
      }
    },

    openSub(item, index) {
      this.mouseEventRecent = true;
      // always close any previously open submenu
      if (this.selectedSub !== null && this.selectedSub !== index) {
        this.$emit('closeSub');
      }
      // toggle current
      this.selectedSub = this.selectedSub === index ? null : index;
      this.$emit(this.selectedSub === null ? 'closeSub' : 'clicked', item);
    },
    handleDocumentClick(event) {
      // if you click anywhere *outside* this nav component, close
      if (this.selectedSub !== null && !this.$el.contains(event.target)) {
        this.selectedSub = null;
        this.$emit('closeSub');
      }
    },
    openSubAndFocus(item, index) {
      this.selectedSub = index;
      this.$emit('clicked', item);
      this.$emit('focusSubmenu', item);
    },
    closeSub() {
      this.selectedSub = null;
      this.$emit('closeSub');
    },
    focusSubmenu(item) {
      this.$emit('focusSubmenu', item);
    },
    isUrlCurrentlyActive(url) {
      return window.location.href.startsWith(url) || window.location.pathname.startsWith(url);
    },
    scrollToElementIfLinkIsLocal(url, event) {
      if (url.startsWith('#')) {
        const targetElement = document.querySelector(url);

        event.preventDefault();

        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth' });
        }
      }
    },
    fireEvent(data) {
      trackEvent(data, this.$gtm.enabled());
    },
    handleFocus(item, index) {
      // Don't trigger submenu focus when focus comes from mouse click
      if (this.mouseEventRecent) {
        this.mouseEventRecent = false;
        return;
      }

      this.selectedSub = index;
      this.$emit('clicked', item);

      setTimeout(() => {
        this.$emit('focusSubmenu', item);
      }, 10);
    },
  },
};
</script>

<style lang="postcss">
.main-navigation {
  @apply w-full items-end flex flex-col justify-between absolute z-10 overflow-hidden left-0 top-0 opacity-0 overflow-y-scroll lg:overflow-visible;
  transform: translateY(-100%);
  transition: all 350ms;

  .first-button {
    @apply border-gray-75 border text-gray-500;

    &.single-button {
      @apply border-primary-bravo text-primary-bravo hover:text-white;
    }
  }

  @screen lg {
    @apply relative h-full top-auto flex flex-row opacity-100;
    height: 55px;
    transform: translateY(0);
    overflow: inherit;
  }

  &.is-open {
    @apply bg-white opacity-100 shadow-md h-[calc(100vh-60px)] w-screen;
    transform: translateY(0);
    top: 60px;

    @screen lg {
      @apply top-0 bg-transparent h-auto;
    }
  }

  &.is-closed {
    @screen lg {
      @apply opacity-100;
      transform: translateY(0);
    }
  }

  &-list {
    @apply relative h-full items-start flex flex-col px-8 list-none w-full pt-8;

    @screen md {
      @apply pt-14;
    }

    @screen lg {
      @apply flex-row pt-0 px-0;
    }

    &-item {
      @apply relative flex flex-col items-center justify-center w-full mt-4;

      &:not(:last-of-type) {
        @apply border-b border-gray-100/25;
      }

      @screen lg {
        @apply justify-end h-full mr-10 w-auto mt-0;
        &:not(:last-of-type) {
          @apply border-b-0;
        }
      }

      &--search {
        @apply justify-end md:hidden;
      }

      &:last-of-type {
        @apply border-b border-gray-100/25;

        @screen lg {
          @apply border-0;
        }
      }
    }

    &-link {
      @apply w-full font-normal mb-4 block no-underline text-base;
      .theme-secondary & {
        &::after {
          background: transparent;
        }
      }

      @screen lg {
        @apply font-normal mb-0 inline text-black pb-4;

        &::after {
          content: '';
          @apply absolute block w-0 h-1;
          border-radius: 1px;
          background: theme('colors.primary.bravo');
          transition: width 0.3s;
          bottom: -1px;
        }

        &:hover {
          @apply no-underline text-black;

          &::after {
            width: 100%;
          }
        }

        &.is-active {
          &::after {
            width: 100%;
            transition: none;
          }
        }
      }

      @screen md {
        @apply text-lg;
      }

      &:focus {
        outline: none;
        box-shadow: none;
      }

      &:focus-visible {
        outline: 2px solid theme('colors.primary.bravo');
        outline-offset: 2px;
      }

      &.is-toggle {
        @apply relative pr-6 text-left;

        &::before {
          @apply absolute bg-no-repeat right-0;
          content: '';
          height: 13px;
          width: 13px;
          top: 7px;
          background-size: 13px 13px;
          background-image: url('@/assets/svg/chevron-down-secondary.svg');
          background-position: right;
          transition: transform 250ms ease;
        }
      }

      .theme-secondary & {
        @apply relative w-full text-primary-bravo font-bold mb-4 block no-underline text-xl;
        position: relative;

        @screen lg {
          @apply mb-0 inline text-black pb-4;
        }

        &:hover {
          color: var(--color-primary-alpha);
          text-decoration: none !important;
        }
        &::before {
          background-image: url('@/assets/svg/theme-secondary/ts-chevron-down-secondary.svg');
        }
      }

      &.is-open {
        &::before {
          transform: rotate(180deg);
        }

        @screen lg {
          &::after {
            content: '';
            @apply absolute block w-full h-1;
            border-radius: 1px;
            background: theme('colors.primary.bravo');
            transition: width 0.3s;
            bottom: -1px;
          }
        }

        + .sub-navigation-list-small {
          @media (max-width: 1023px) {
            @apply block;
          }

          @media (min-width: 1024px) {
            @apply hidden;
          }
        }
      }
    }

    .sub-navigation-list-small {
      @apply hidden px-7 w-full text-left;

      &.is-visible {
        @media (max-width: 1023px) {
          @apply block;
        }
      }

      /* Always hide on desktop */
      @media (min-width: 1024px) {
        @apply hidden;
      }

      .sub-navigation-list-item-small {
        @apply mb-3;
      }

      .sub-navigation-list-link-small {
        @apply no-underline text-primary-bravo;
      }
    }
  }

  &-end {
    @apply flex;

    .link.is-button-primary {
      @apply py-2 mb-3 ml-4 hidden;

      @screen lg {
        @apply flex text-base;
      }
    }
  }

  .search-bar-wrapper-small {
    @apply flex mb-2;

    @screen lg {
      @apply hidden;
    }
  }
}
</style>
